import { CodeOutlined, EditOutlined, SnippetsOutlined, SwapOutlined } from "@ant-design/icons";
import { json } from "@codemirror/lang-json";
import CodeMirror from "@uiw/react-codemirror";
import { useDeferredValue, useMemo, useState } from "react";
import { formatJson, parseInput } from "./jsonUtils";
import type { JsonValue } from "./jsonUtils";

const parseComparisonInput = (input: string, label: string): JsonValue => {
    try {
        // Keep commas inside quoted strings; only remove commas before a closing bracket.
        const normalizedInput = input.replace(
            /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|,(?=\s*[}\]])/g,
            token => token === "," ? "" : token,
        );
        return parseInput(normalizedInput);
    } catch (error) {
        throw new Error(`${label}: ${error instanceof Error ? error.message : "Invalid JSON"}`);
    }
};

interface ObjectDiffRow {
    before?: string;
    after?: string;
    beforeLine?: number;
    afterLine?: number;
    changed: boolean;
}

const getObjectDiffRows = (before: string, after: string): ObjectDiffRow[] => {
    const left = before.split("\n");
    const right = after.split("\n");
    const rows: ObjectDiffRow[] = [];
    let prefix = 0;
    while (prefix < left.length && prefix < right.length && left[prefix] === right[prefix]) {
        rows.push({ before: left[prefix], after: right[prefix], beforeLine: prefix + 1, afterLine: prefix + 1, changed: false });
        prefix += 1;
    }
    let leftEnd = left.length;
    let rightEnd = right.length;
    while (leftEnd > prefix && rightEnd > prefix && left[leftEnd - 1] === right[rightEnd - 1]) {
        leftEnd -= 1;
        rightEnd -= 1;
    }
    const removed: number[] = [];
    const added: number[] = [];
    const flush = () => {
        for (let index = 0; index < Math.max(removed.length, added.length); index += 1) {
            const beforeIndex = removed[index];
            const afterIndex = added[index];
            rows.push({ before: beforeIndex === undefined ? undefined : left[beforeIndex], after: afterIndex === undefined ? undefined : right[afterIndex], beforeLine: beforeIndex === undefined ? undefined : beforeIndex + 1, afterLine: afterIndex === undefined ? undefined : afterIndex + 1, changed: true });
        }
        removed.length = 0;
        added.length = 0;
    };
    const leftCount = leftEnd - prefix;
    const rightCount = rightEnd - prefix;
    // Bound memory for very large objects; preserve their common prefix and suffix.
    if ((leftCount + 1) * (rightCount + 1) <= 1_000_000) {
        const lengths = Array.from({ length: leftCount + 1 }, () => new Uint32Array(rightCount + 1));
        for (let i = leftCount - 1; i >= 0; i -= 1) {
            for (let j = rightCount - 1; j >= 0; j -= 1) {
                lengths[i][j] = left[prefix + i] === right[prefix + j] ? lengths[i + 1][j + 1] + 1 : Math.max(lengths[i + 1][j], lengths[i][j + 1]);
            }
        }
        let i = 0;
        let j = 0;
        while (i < leftCount || j < rightCount) {
            if (i < leftCount && j < rightCount && left[prefix + i] === right[prefix + j]) {
                flush();
                rows.push({ before: left[prefix + i], after: right[prefix + j], beforeLine: prefix + i + 1, afterLine: prefix + j + 1, changed: false });
                i += 1;
                j += 1;
            } else if (i < leftCount && (j === rightCount || lengths[i + 1][j] >= lengths[i][j + 1])) {
                removed.push(prefix + i++);
            } else {
                added.push(prefix + j++);
            }
        }
    } else {
        for (let i = prefix; i < leftEnd; i += 1) removed.push(i);
        for (let j = prefix; j < rightEnd; j += 1) added.push(j);
    }
    flush();
    while (leftEnd < left.length && rightEnd < right.length) {
        rows.push({ before: left[leftEnd], after: right[rightEnd], beforeLine: ++leftEnd, afterLine: ++rightEnd, changed: false });
    }
    return rows;
};

const DiffLine = ({ value, other, added }: { value: string; other?: string; added: boolean }) => {
    if (other === undefined) return <>{value || " "}</>;
    let start = 0;
    while (start < value.length && start < other.length && value[start] === other[start]) start += 1;
    let end = value.length;
    let otherEnd = other.length;
    while (end > start && otherEnd > start && value[end - 1] === other[otherEnd - 1]) { end -= 1; otherEnd -= 1; }
    return <>{value.slice(0, start)}<span className={added ? "rounded-sm bg-emerald-400/25" : "rounded-sm bg-rose-400/25"}>{value.slice(start, end)}</span>{value.slice(end) || (value ? "" : " ")}</>;
};

const CompareObject = () => {
    const [mobileSection, setMobileSection] = useState<"original" | "updated" | "changes">("original");
    const [editorsExpanded, setEditorsExpanded] = useState(true);
    const [clipboardError, setClipboardError] = useState("");
    const pasteObject = async (onChange: (value: string) => void) => {
        try {
            const text = await navigator.clipboard.readText();
            onChange(text);
            setClipboardError("");
        } catch {
            setClipboardError("Unable to read clipboard. Please allow clipboard access and try again.");
        }
    };
    const [left, setLeft] = useState("");
    const [right, setRight] = useState("");
    const deferredLeft = useDeferredValue(left);
    const deferredRight = useDeferredValue(right);
    const result = useMemo(() => {
        if (!deferredLeft.trim() || !deferredRight.trim()) return { rows: [], error: "" };
        try {
            return { rows: getObjectDiffRows(formatJson(parseComparisonInput(deferredLeft, "Original object")), formatJson(parseComparisonInput(deferredRight, "Updated object"))), error: "" };
        } catch (error) {
            return { rows: [], error: error instanceof Error ? error.message : "Invalid JSON" };
        }
    }, [deferredLeft, deferredRight]);
    const removedCount = result.rows.filter(row => row.changed && row.before !== undefined).length;
    const addedCount = result.rows.filter(row => row.changed && row.after !== undefined).length;
    const pending = deferredLeft !== left || deferredRight !== right;
    return (
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden sm:block sm:overflow-auto">
            <div className={`${mobileSection === "changes" ? "hidden" : "flex"} min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[#373b44] bg-[#202329] max-sm:h-full sm:mb-5 sm:block sm:overflow-visible`}>
                <div className="grid h-full min-h-0 flex-1 gap-px overflow-hidden rounded-lg bg-[#373b44] sm:h-auto lg:grid-cols-2">
                    {[{ key: "original", label: "Original object", value: left, onChange: setLeft }, { key: "updated", label: "Updated object", value: right, onChange: setRight }].map(editor => (
                        <div className={`${mobileSection === editor.key ? "flex" : "hidden"} min-h-0 min-w-0 flex-col bg-[#24272e] sm:flex`} key={editor.label}>
                            <div className={`flex h-[42px] shrink-0 items-center justify-between border-b border-[#373b44] px-2.5 sm:h-[50px] sm:px-5 ${editorsExpanded ? "" : "sm:border-b-0"}`}>
                                <h2 className="m-0 flex h-full flex-1 text-sm font-semibold text-slate-300">
                                    <span className="flex items-center sm:hidden">{editor.label}</span>
                                    <button aria-expanded={editorsExpanded} className="hidden h-full w-full items-center text-left sm:flex" onClick={() => setEditorsExpanded(expanded => !expanded)} type="button">{editor.label}</button>
                                </h2>
                                <button aria-label={`Paste ${editor.label.toLowerCase()}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-indigo-500/10 hover:text-indigo-400" onClick={() => pasteObject(editor.onChange)} title="Paste from clipboard" type="button"><SnippetsOutlined /></button>
                            </div>
                            <div className={`flex min-h-0 flex-1 overflow-hidden sm:flex-none sm:transition-[height,opacity,visibility] sm:duration-300 sm:ease-in-out motion-reduce:transition-none ${editorsExpanded ? "sm:h-[220px] sm:opacity-100" : "sm:h-0 sm:opacity-0 sm:invisible"}`}>
                                <CodeMirror aria-label={editor.label} className="min-h-0 min-w-0 flex-1 [&_.cm-scroller]:overscroll-auto sm:h-[220px]" extensions={[json()]} height="100%" onChange={editor.onChange} placeholder="Paste JSON object..." theme="dark" value={editor.value} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {clipboardError && <p role="alert" className="my-2 shrink-0 text-xs text-rose-400">{clipboardError}</p>}
            <div className={`${mobileSection === "changes" ? "flex" : "hidden"} min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[#373b44] bg-[#202329] sm:block`}>
                <div aria-live="polite" className="flex min-h-[42px] shrink-0 flex-wrap items-center gap-2 sm:gap-4 border-b border-[#373b44] px-2.5 py-0 text-sm font-semibold sm:h-[50px] sm:px-5">
                    <span className="text-slate-200">Changes</span>
                    <span className="text-rose-400">−{removedCount} lines</span>
                    <span className="text-emerald-400">+{addedCount} lines</span>
                    {pending && <span className="text-xs text-slate-500">Comparing...</span>}
                </div>
                {result.error ? <p role="alert" className="m-0 p-5 text-rose-400">{result.error}</p> : !left.trim() || !right.trim() ? <p className="m-0 p-5 text-slate-400">Enter two JSON objects to compare.</p> : <>
                    {!removedCount && !addedCount && <p className="m-0 p-4 text-emerald-400">Objects are identical.</p>}
                    <div className={`min-h-0 flex-1 overflow-auto ${pending ? "opacity-60" : ""}`}>
                        <table className="w-full min-w-[700px] table-fixed border-collapse font-mono sm:min-w-0 text-xs leading-6">
                            <thead><tr className="text-left text-slate-400"><th className="w-1/2 border-r border-[#373b44] px-4 py-2 font-sans">Original object</th><th className="w-1/2 px-4 py-2 font-sans">Updated object</th></tr></thead>
                            <tbody>{result.rows.map((row, index) => <tr key={index}>
                                {([false, true] as const).map(added => {
                                    const value = added ? row.after : row.before;
                                    const line = added ? row.afterLine : row.beforeLine;
                                    return <td className={`border-r border-[#373b44] p-0 align-top last:border-r-0 ${row.changed ? value === undefined ? "bg-slate-950/40" : added ? "bg-emerald-500/10 text-emerald-200" : "bg-rose-500/10 text-rose-200" : "text-slate-400"}`} key={String(added)}>
                                        <div className="flex min-h-6">
                                            <span className="w-12 shrink-0 select-none border-r border-white/5 pr-2 text-right text-slate-500">{line}</span>
                                            <span className="w-6 shrink-0 select-none text-center">{row.changed && value !== undefined ? added ? "+" : "−" : ""}</span>
                                            <code className="min-w-0 flex-1 whitespace-pre pr-3 sm:whitespace-pre-wrap sm:break-all">{value !== undefined && (row.changed ? <DiffLine added={added} other={added ? row.before : row.after} value={value} /> : value || " ")}</code>
                                        </div>
                                    </td>;
                                })}
                            </tr>)}</tbody>
                        </table>
                    </div>
                </>}
            </div>
            <nav aria-label="Compare object panels" className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-3 border-t border-[#373b44] bg-[#202329] pb-[env(safe-area-inset-bottom)] sm:hidden">
                {([{ key: "original", label: "Original", icon: <CodeOutlined /> }, { key: "updated", label: "Updated", icon: <EditOutlined /> }, { key: "changes", label: "Changes", icon: <SwapOutlined /> }] as const).map(panel => (
                    <button aria-pressed={mobileSection === panel.key} className={`flex h-12 items-center justify-center gap-2 whitespace-nowrap text-xs font-semibold transition-colors [&_.anticon]:text-base ${mobileSection === panel.key ? "text-indigo-300" : "text-slate-400 hover:text-slate-200"}`} key={panel.key} onClick={() => setMobileSection(panel.key)} type="button">{panel.icon}{panel.label}</button>
                ))}
            </nav>
        </section>
    );
};


export default CompareObject;
